import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import TradeContext from '~/context/common/TradeContext';
import { Trade } from '~/contracts/common';
import { fetchTrades } from '~/services/common';
import generateErrorLogger from '~/utilities/errorHandlers';

const activeStates = ['selected', 'visible'];
const inactiveStatuses = ['completed', 'deleted'];

const useGetTrades = (shouldGetTrades: boolean) => {
  const {
    setActiveTrade,
    setStatusAgnosticTrade,
    trades: { setData, setError, setLoading }
  } = useContext(TradeContext);

  useEffectWithController(
    controller => {
      setLoading();
      if (!shouldGetTrades) return setData([]);

      fetchTrades(controller)
        .then(res => {
          const allAppraisals = res.data.content
            ? res.data.content
                .filter(
                  ({ displayPreference, status, vehicleId }) =>
                    vehicleId &&
                    activeStates.includes(displayPreference.toLowerCase()) &&
                    !inactiveStatuses.includes(status.toLowerCase())
                )
                .map(({ effectiveDate, ...appraisal }) => ({
                  effectiveDate: Date.parse(effectiveDate),
                  ...appraisal
                }))
                .sort((a, b) => b.effectiveDate - a.effectiveDate)
            : [];

          if (allAppraisals.length > 0) {
            setStatusAgnosticTrade(allAppraisals[0]);
          }

          const activeAppraisals = allAppraisals.filter(
            (a: Trade) => a.status === 'Active'
          );

          if (activeAppraisals.length > 0) {
            setActiveTrade(activeAppraisals[0]);
          }

          setData(allAppraisals);
        })
        .catch(generateErrorLogger('Get trades', {}, { callback: setError }));
    },
    [
      setActiveTrade,
      setStatusAgnosticTrade,
      setData,
      setError,
      setLoading,
      shouldGetTrades
    ]
  );
};

export default useGetTrades;
